import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase/client';

export interface Branding {
  companyId: string;
  name: string;
  productName: string;
  tagline: string;
  logoUrl: string;
  domain: string;
  supportEmail: string;
  palette: {
    ink: string;
    midnight: string;
    electric: string;
    plasma: string;
    amber: string;
    mint: string;
  };
}

const BrandingContext = createContext<Branding | null>(null);

const COMPANY_ID = import.meta.env.VITE_COMPANY_ID || '61f0ab7c-fb9b-4c0e-8411-adf3d35c72a5';

function applyPalette(palette: Branding['palette']) {
  const root = document.documentElement.style;
  root.setProperty('--brand-ink', palette.ink);
  root.setProperty('--brand-midnight', palette.midnight);
  root.setProperty('--brand-electric', palette.electric);
  root.setProperty('--brand-plasma', palette.plasma);
  root.setProperty('--brand-amber', palette.amber);
  root.setProperty('--brand-mint', palette.mint);
}

// Static favicons in public/ ship to every tenant, so swap them at runtime
// using the company's logo_url from b2b_company_branding.
function applyFavicon(logoUrl: string) {
  if (!logoUrl) return;
  document
    .querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]')
    .forEach((el) => el.remove());

  const icon = document.createElement('link');
  icon.rel = 'icon';
  icon.href = logoUrl;
  document.head.appendChild(icon);

  const apple = document.createElement('link');
  apple.rel = 'apple-touch-icon';
  apple.href = logoUrl;
  document.head.appendChild(apple);
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<Branding | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      // b2b_company_branding is a public view of non-sensitive branding
      // columns; readable by anon so the shell renders before sign-in.
      const { data: raw, error: err } = await supabase
        .from('b2b_company_branding' as never)
        .select('*')
        .eq('id', COMPANY_ID)
        .maybeSingle();

      if (err || !raw) {
        setError(err?.message || 'Company not found');
        return;
      }

      const data = raw as unknown as Record<string, string | null>;

      const b: Branding = {
        companyId: data.id!,
        name: data.name!,
        productName: data.product_name || data.name!,
        tagline: data.tagline || '',
        logoUrl: data.logo_url || '',
        domain: data.portal_url?.replace(/^https?:\/\//, '') || '',
        supportEmail: data.support_email || '',
        palette: {
          ink: data.bg_dark || '#0A0B14',
          midnight: data.bg_card || '#11132B',
          electric: data.accent_primary || '#5B7CFF',
          plasma: data.accent_secondary || '#8B5CF6',
          amber: data.accent_warning || '#F5A524',
          mint: data.accent_success || '#34D399',
        },
      };

      applyPalette(b.palette);
      applyFavicon(b.logoUrl);
      document.title = b.productName;
      setBranding(b);
    }

    load();
  }, []);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0B14] text-white">
        <div className="text-center">
          <p className="text-lg font-semibold">Portal unavailable</p>
          <p className="mt-2 text-sm text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  if (!branding) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0B14]">
        <div className="animate-pulse text-center">
          <div className="mx-auto h-10 w-40 rounded-lg bg-white/5" />
          <div className="mx-auto mt-3 h-3 w-24 rounded bg-white/5" />
        </div>
      </div>
    );
  }

  return <BrandingContext.Provider value={branding}>{children}</BrandingContext.Provider>;
}

export function useBranding(): Branding {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used inside BrandingProvider');
  return ctx;
}
